import { createTaskRunner } from '@gonfalon/async/createTaskRunner';
import { chunk } from '@gonfalon/collections/chunk';
import { flagStatusesEnvironmentKeyLimit, flagStatusesFlagKeyLimit } from '@gonfalon/dogfood-flags/dogfood-flags';
import { postFeatureFlagStatusesQuery } from '@gonfalon/openapi/operations/postFeatureFlagStatusesQuery';
import { queryFeatureFlagStatuses } from '@gonfalon/openapi/operations/queryFeatureFlagStatuses';
import type { schemas } from '@gonfalon/openapi/types';

import { mergeEnvironmentsByKey } from './mergeEnvironmentsByKey';
import { reactQueryResponseAdapter } from './reactQueryResponseAdapter';

export type FlagStatusTask = {
  projectKey: string;
  flagKey: string;
  environmentKeys: string[];
};

type FlagStatusQueryFn = typeof queryFeatureFlagStatuses | typeof postFeatureFlagStatusesQuery;

/**
 * Factory function to create a task runner for flag statuses.
 * @param queryFn - The API endpoint function to use
 * @returns A configured task runner
 */
function createFlagStatusTaskRunner(queryFn: FlagStatusQueryFn) {
  return createTaskRunner<
    FlagStatusTask,
    Record<string, schemas['FeatureFlagStatus']>,
    NonNullable<Awaited<ReturnType<FlagStatusQueryFn>>['data']>
  >({
    runner: async (inputs) =>
      reactQueryResponseAdapter(
        queryFn({
          projectKey: inputs[0].projectKey,
          body: {
            flagKeys: Array.from(new Set(inputs.map((input) => input.flagKey))),
            environmentKeys: Array.from(new Set(inputs.map((input) => input.environmentKeys).flat())),
          },
        }),
      ),
    resolver: (input, task) => {
      const { items } = input;
      const flag = items.find((item) => item.key === task.flagKey);

      if (!flag) {
        return {};
      }

      const status: Record<string, schemas['FeatureFlagStatus']> = {};
      for (const environmentKey of task.environmentKeys) {
        const value = flag.environments[environmentKey];
        if (!value) {
          continue;
        }
        status[environmentKey] = value;
      }

      return status;
    },
    batching: {
      batcher: queryFeatureFlagStatusesTaskBatcher,
      resultMerger(data) {
        return {
          _links: data[0]._links,
          items: mergeEnvironmentsByKey(data),
        };
      },
    },
  });
}

/**
 * Task runner using the old endpoint (strict validation, no partial return).
 * Uses `/flag-statuses/queries` which returns 404 if any requested flag is missing.
 */
export const queryFeatureFlagStatusesTaskRunner = createFlagStatusTaskRunner(queryFeatureFlagStatuses);

/**
 * Task runner using the new endpoint (partial return support).
 * Uses `/flag-statuses/query` which returns partial results when some flags are missing.
 *
 * Use this for UI components that should gracefully handle missing flag statuses (e.g., flag environment cards).
 * This is more resilient to timing issues during flag creation.
 */
export const queryFeatureFlagStatusesWithPartialReturnTaskRunner =
  createFlagStatusTaskRunner(postFeatureFlagStatusesQuery);

export function queryFeatureFlagStatusesTaskBatcher(inputs: FlagStatusTask[]) {
  const flagKeyChunks = chunk(Array.from(new Set(inputs.map((input) => input.flagKey))), flagStatusesFlagKeyLimit());
  const environmentKeyChunks = chunk(
    Array.from(new Set(inputs.map((input) => input.environmentKeys).flat())),
    flagStatusesEnvironmentKeyLimit(),
  );

  const batches: FlagStatusTask[][] = [];

  environmentKeyChunks.forEach((chunkOfEnvironmentKeys) => {
    flagKeyChunks.forEach((chunkOfFlagKeys) => {
      const tasks: FlagStatusTask[] = [];
      chunkOfFlagKeys.forEach((flagKey) => {
        tasks.push({ projectKey: inputs[0].projectKey, flagKey, environmentKeys: chunkOfEnvironmentKeys });
      });
      batches.push(tasks);
    });
  });

  return batches;
}
